import React, { FC, useEffect, useRef, useState } from 'react'
import { TFunction } from 'next-i18next'
import {
  Button
} from '@viadelivery/design-system'
import Octicon, { Gear } from '@primer/octicons-react'
import DateFormatPicker from '~/components/OrdersPage/DateFormatPicker'
import DatePickerColumn from '~/components/OrdersPage/DatePickerColumn'
import Icon from '~/utils/Icon'
import SortingPicker from '~/components/OrdersPage/SortingPicker'
import Switch from '~/components/Switch/Switch'
import Timezones from '~/components/OrdersPage/Timezones'
import UserSettings from '~/utils/UserSettings'
import styles from '~/styles/ordersPage.tableSettingsDropdown.module.css'

import styled from 'styled-components'

interface Props {
  allColumns: object[],
  shownColumns: string[],
  handleChange: (e) => void,
  t: TFunction
}

const StyledTableSettingsDropdown = styled.div`
  position: static;
`

const StyledDropdown = styled.div`
  min-width: 200px;
  z-index: 3;
`

let dropdownCount = 0

const timezoneName = 'timezones'
let timezonesToUpdate

const TableSettingsDropdown: FC<Props> = (props) => {
  const [ saveButton, setSaveButton ] = useState(null)
  const [ showHierarchyState, setShowHierarchyState ] = useState(props.showHierarchy)
  const [ datePickerColumnState, setdDatePickerColumnState ] = useState(props.datePickerColumn)
  const [ isHoursAndMinutesShownState, setIsHoursAndMinutesShownState ] = useState(props.isHoursAndMinutesShown)
  const [ timezoneCodeState, setTimezoneCodeState ] = useState(props.timezoneCode)
  const [ timezoneNameState, setTimezoneNameState ] = useState(props.timezoneName)
  const [ shownColumnsState, setShownColumnsState ] = useState(props.shownColumns || [])

  const onDatePickerColumnChange = (value) => {
    setdDatePickerColumnState(value)
  }
  const onHoursAndMinutesShownChange = (value) => {
    setIsHoursAndMinutesShownState(value)
  }
  const onTimezoneCodeChange = (value) => {
    setTimezoneCodeState(value)
  }
  const onTimezoneNameChange = (value) => {
    setTimezoneNameState(value)
  }

  const onTimezonesToUpdate = (callback) => {
    timezonesToUpdate = callback
  }

  const onSave = () => {
    setSaveButton(null)
    props.setOrderListUpdater(Date.now())
    props.setShowHierarchy(showHierarchyState)
    props.setDatePickerColumn(datePickerColumnState)
    props.setIsHoursAndMinutesShown(isHoursAndMinutesShownState)
    props.setTimezoneCode(timezoneCodeState)
    props.setTimezoneName(timezoneNameState)
    if (timezonesToUpdate) {
      timezonesToUpdate(true)
    }
    props.setShownColumns(shownColumnsState)
    closeButtonRef.current.click()
    setTimeout(() => {
      props.setOrderListUpdater(Date.now())
    })
  }

  const closeButtonRef = useRef()
  if (!(props.clickLabel + '').startsWith('ordersTableSettings')) {
    dropdownCount = 0
  }
	const onClick = () => {
    dropdownCount++
	}

  const isOpened = ((props.clickLabel + '').startsWith('ordersTableSettings') && dropdownCount%2 !== 0)
  let backgroundClass = 'bg-transparent'
  if (isOpened) {
    backgroundClass = 'bg-white'
  }

  useEffect(() => {
    setShowHierarchyState(props.showHierarchy)
    setdDatePickerColumnState(props.datePickerColumn) 
    setIsHoursAndMinutesShownState(props.isHoursAndMinutesShown)
    setTimezoneCodeState(props.timezoneCode)
    setTimezoneNameState(props.timezoneName)
    setShownColumnsState(props.shownColumns)
    if (timezonesToUpdate) {
      timezonesToUpdate()
    }
    onChangeForUserSettings()
  }, [ isOpened ])

  const onSwitchChange = (bool) => {
    setShowHierarchyState(bool)
    props.setIsHierarchyBeenClicked(true)
    onChangeForUserSettings('isHierarchyShown', bool)
  }

  const updateShownColumns = (event) => {
    const unique = {}
    shownColumnsState.forEach(id => {
      unique[id] = true
    })
    if (event.target.checked) {
      unique[event.target.id] = true
    } else {
      if (unique[event.target.id]) {
        delete unique[event.target.id]
      }
    }
    setShownColumnsState(Object.keys(unique))
    onChangeForUserSettings('shownColumns', Object.keys(unique))
  }

  const onChangeForUserSettings = (name, value) => {
    let currentSaveButton = JSON.parse(JSON.stringify(saveButton || { id: 'mainSettings', value: {} }))
    if (name) {
      currentSaveButton.value.[name] = value
    } else {
      currentSaveButton = null
    }
    setSaveButton(currentSaveButton)
  }

  const onSaveButton = () => {
    setSaveButton(null)
  }

  let disableClass = null
  if (props.isOrdersFetching) {
    disableClass = styles.disabled
  }

  return (
    <div
       clicklabel="ordersTableSettings"
    >
      <StyledTableSettingsDropdown>
        <Button 
          variant="info"
          className={"border border-gray-400 " + backgroundClass + ' ' + styles.gearButton}
          onClick={onClick}
        >
          <Octicon
            icon={Gear}
          />
        </Button>
        {isOpened &&
          <div
            className={styles.styledDropdown}
            clicklabel="antiOrdersTableSettingsDropdown"
          >
            <div
              clicklabel="ordersTableSettings"
            >
              <div className={styles.dropdownFrame}>
                <div
                  className={styles.close}
                  clicklabel="closeTableSettings"
                  ref={closeButtonRef}
                >
                  <Icon
                    iconName="close"
                  />
                </div>
                <div
                  className={styles.saveButton}
                >
                  <UserSettings
                    type='multiple'
                    id='mainSettings'
                    saveButton={saveButton}
                    setSaveButton={onSaveButton}
                    onSave={onSave}
                    t={props.t}
                  />
                </div>
                <div
                  className={styles.line}
                >
                  <div
                    className={styles.listMode}
                  >
                    <div className={styles.title}>
                      {props.t('List type mode')}:
                    </div>
                    <div className={styles.flatToHierarchy}>
                      <div 
                        className={disableClass + (showHierarchyState ? '' : ' ' + styles.switchActive)}
                        onClick={onSwitchChange.bind(this, false)}
                      >
                        <div>
                          {props.t('Flat')}
                        </div>
                      </div>
                      <Switch
                        isOn={showHierarchyState}
                        isBlueNo={true}
                        isGreenYes={true}
                        isDisabled={props.isOrdersFetching}
                        onChange={onSwitchChange}
                      />
                      <div
                        className={disableClass + (showHierarchyState ? ' ' + styles.switchActive : '')}
                        onClick={onSwitchChange.bind(this, true)}
                      >
                        <div>
                          {props.t('Hierarchy')}
                        </div>
                      </div>
                    </div>
                  </div>
                  <DatePickerColumn
                    isShown={true}
                    datePickerColumn={datePickerColumnState}
                    setDatePickerColumn={onDatePickerColumnChange}
                    isDisabled={props.isOrdersFetching}
                    onAction={props.onAction}
                    onChange={onChangeForUserSettings.bind(this, 'datePickerColumn')}
                    isLocalStorageInUse={false}
                    t={props.t}
                  />
                </div>
                <div
                  className={styles.line}
                >
                  <DateFormatPicker
                    isHoursAndMinutesShown={isHoursAndMinutesShownState}
                    setIsHoursAndMinutesShown={onHoursAndMinutesShownChange}
                    onAction={props.onAction}
                    onChange={onChangeForUserSettings.bind(this, 'isTimeShown')}
                    t={props.t}
                  />
                  <Timezones
                    name={timezoneName}
                    toShow='picker'
                    timezoneCode={timezoneCodeState}
                    setTimezoneCode={onTimezoneCodeChange}
                    timezoneName={timezoneNameState}
                    setTimezoneName={onTimezoneNameChange}
                    clickLabel={props.clickLabel}
                    onAction={props.onAction}
                    onTimezoneUpdate={onChangeForUserSettings.bind(this, timezoneName)}
                    onTimezonesUpdate={onTimezonesToUpdate.bind(this)}
                    isRegionsShown={false}
                    t={props.t}
                  />
                </div>
                <div
                  className={styles.ulFrame}
                >
                  <div
                    className={styles.ulTitle + ' ' + styles.title}
                  >
                    {props.t('Orders:Shown columns')}:
                  </div>
                  <ul
                    className={styles.ul}
                  >
                    {props.allColumns.map((column: any) => 
                      <li
                        key={column.accessor}
                        className="text-left"
                        clicklabel="ordersTableSettingsLi"
                      >
                        <label className="px-4 block cursor-pointer hover:bg-gray-100">
                          <input
                            type='checkbox'
                            id={column.accessor}
                            checked={shownColumnsState.indexOf(column.accessor) !== -1}
                            onChange={updateShownColumns}
                          />
                          <span className="pl-3">{props.t(column.Header)}</span>
                        </label>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        }
      </StyledTableSettingsDropdown>
    </div>
  )
}

export default TableSettingsDropdown